import { IconButton, Stack, Typography, useTheme } from "@mui/material";
import { ArrowDualIcon, CircularLoader, Drawer, Tooltip } from "components";
import { useGetAuditTrail, useGlobalStore } from "controller";
import { useEffect, useMemo, useState } from "react";
import type { ServiceAuditSource } from "shared";
import { capitalizeString } from "utilz/helpers";
import { Sentry } from "utilz/sentry";
import {
  type AuditFormatOptions,
  describeAuditEntry,
} from "./timeline/auditEventCatalog";
import { formatAuditTimestamp } from "./timeline/formatAuditTimestamp";
import {
  isUnknownAuditSource,
  parseAuditSource,
} from "./timeline/parseAuditSource";
import {
  type AuditSortDirection,
  sortAuditEntries,
} from "./timeline/sortAuditEntries";
import {
  type AuditLineSegment,
  collectEmphasisTokens,
  splitAuditLine,
} from "./timeline/splitAuditLine";
import { TimelineRow } from "./timeline/TimelineRow";

interface TimelineProps {
  open: boolean;
  onClose: () => void;
  serviceId: string;
  serviceType: "ticket" | "project" | "task";
}

/** One rendered line — the unit of this list, which is NOT the audit entry. */
interface TimelineRowData {
  id: string;
  segments: AuditLineSegment[];
  actorName: string;
  actorAvatarUrl?: string;
  timestamp: string;
  /** Drives the avatar-vs-glyph choice in `TimelineRow`. */
  source?: ServiceAuditSource | null;
}

/** Design spec. Height is deliberately unset — the drawer fills its viewport. */
const DRAWER_WIDTH = "500px";

/**
 * Source values already reported this session.
 *
 * Module-level so reopening the drawer, or opening it on another ticket, does
 * not re-report the same drift — one Sentry event per unrecognised value is the
 * signal; one per render is noise.
 */
const reportedUnknownSources = new Set<string>();

export const TimelineDrawer = ({
  open,
  onClose,
  serviceId,
  serviceType,
}: TimelineProps) => {
  const theme = useTheme();
  const timezone = useGlobalStore((state) => state.timezone);
  const dateFormat = useGlobalStore((state) => state.dateFormat);

  /** Newest first by default — the most recent activity is what's being asked about. */
  const [sortDirection, setSortDirection] =
    useState<AuditSortDirection>("desc");

  const {
    data: auditTrailData,
    isLoading,
    isError,
  } = useGetAuditTrail({ serviceId, serviceType, enabled: open });

  const formatOptions: AuditFormatOptions = useMemo(
    () => ({
      serviceType,
      dateFormat: dateFormat || "MM/DD/YYYY",
      timezone,
    }),
    [serviceType, dateFormat, timezone],
  );

  /**
   * One row per **sentence**, not per audit entry: a single "details updated"
   * entry can carry several changes, and the catalog already emits each as a
   * self-contained sentence including the actor. Flattening here is what lets
   * the list read as a plain narrative with no accordion to open.
   */
  const rows = useMemo<TimelineRowData[]>(() => {
    // Entries are sorted BEFORE flattening — see `sortAuditEntries` for why the
    // toggle can't just reverse the finished row list.
    const entries = sortAuditEntries(auditTrailData ?? [], sortDirection);

    return entries.flatMap((entry) => {
      const view = describeAuditEntry(entry, formatOptions);
      // Names and changed values, read off the entry — the same fields the
      // catalog built its sentences from, so the two-tone split can't drift
      // from the text it's colouring.
      const tokens = collectEmphasisTokens(entry);
      const timestamp = formatAuditTimestamp(entry.created_at, formatOptions);
      return view.lines.map((line, index) => ({
        // Sentences can legitimately repeat inside one entry (the same field
        // changed twice to identical states), so the text alone isn't unique.
        // Order within an entry is fixed, which makes the index stable.
        id: `${entry._id}-${index}`,
        segments: splitAuditLine(line, tokens),
        actorName: entry.activity_by?.name ?? "",
        actorAvatarUrl: entry.activity_by?.profile_pic ?? undefined,
        timestamp,
        // Parsed, not passed through: `source` is whatever the service sent,
        // and `TimelineRow` needs a value it can actually draw a glyph for.
        source: parseAuditSource(entry.source),
      }));
    });
  }, [auditTrailData, formatOptions, sortDirection]);

  /**
   * Reports a `source` the timeline has no copy or glyph for.
   *
   * `parseAuditSource` makes such an entry render correctly — as the human who
   * performed it — which means the drift is invisible from the UI. Without this
   * we would only learn the service had changed its contract when someone
   * noticed a row looked wrong, which is how the uuid-in-`source` bug was found
   * in the first place.
   */
  useEffect(() => {
    for (const entry of auditTrailData ?? []) {
      const raw = entry.source;
      if (!isUnknownAuditSource(raw) || reportedUnknownSources.has(raw ?? "")) {
        continue;
      }
      reportedUnknownSources.add(raw ?? "");
      Sentry.captureMessage(
        `Audit trail: unrecognised source "${raw}" — rendering as manual`,
        "warning",
      );
    }
  }, [auditTrailData]);

  const sortLabel =
    sortDirection === "desc" ? "Sort by oldest first" : "Sort by newest first";

  return (
    <Drawer
      // `serviceType` is the raw union value, so the old
      // `${serviceType} Timeline` rendered "ticket Timeline". The drawer serves
      // projects and tasks too, so the noun stays derived rather than hardcoded.
      title={`${capitalizeString(serviceType)} timeline`}
      keepOriginalCasing
      onClose={onClose}
      onOpen={() => {}}
      open={open}
      footer={<></>}
      drawerStyle={{ width: { xs: "100%", sm: DRAWER_WIDTH } }}
      headerRightActionComponent={
        <Tooltip title={sortLabel} placement="top">
          <IconButton
            aria-label={sortLabel}
            onClick={() =>
              setSortDirection((prev) => (prev === "desc" ? "asc" : "desc"))
            }
            size="small"
          >
            <ArrowDualIcon
              sx={{ fontSize: 20, color: theme.palette.lightBg.high }}
            />
          </IconButton>
        </Tooltip>
      }
    >
      {/* Design spec: 16px top/bottom, 4px between rows. Horizontal padding
          keeps the drawer's existing 24px gutter. */}
      <Stack sx={{ padding: "16px 24px", gap: "4px" }}>
        {isLoading ? (
          <Stack sx={{ p: "32px", alignItems: "center" }}>
            <CircularLoader size={24} />
          </Stack>
        ) : isError ? (
          <Stack sx={{ p: "32px", alignItems: "center" }}>
            <Typography variant="body-medium" color={theme.palette.error.main}>
              Failed to load timeline.
            </Typography>
          </Stack>
        ) : rows.length === 0 ? (
          <Stack sx={{ p: "32px", alignItems: "center" }}>
            <Typography variant="body-medium" color={theme.palette.lightBg.low}>
              No timeline entries yet.
            </Typography>
          </Stack>
        ) : (
          rows.map((row, index) => (
            <TimelineRow
              key={row.id}
              segments={row.segments}
              actorName={row.actorName}
              actorAvatarUrl={row.actorAvatarUrl}
              timestamp={row.timestamp}
              source={row.source}
              isLast={index === rows.length - 1}
            />
          ))
        )}
      </Stack>
    </Drawer>
  );
};
